import React from 'react';
import { Check, RefreshCcw, Save } from 'lucide-react';

import PriceDistributionView from '../PriceDistributionView';
import { formatConfidencePercent } from '../../utils/formatters';

const conditionBadgeClass = (condition) => {
  if (condition === 'New') return 'bg-green-100 text-green-700';
  if (condition === 'Damaged' || condition === 'For Parts') return 'bg-red-100 text-red-700';
  if (condition === 'Unknown') return 'bg-slate-100 text-slate-500';
  return 'bg-yellow-100 text-yellow-700';
};

const sourceBadge = (priceSource) => {
  if (priceSource === 'ebay_rag') return { label: 'eBay RAG', className: 'bg-blue-100 text-blue-700' };
  if (priceSource === 'image') return { label: 'Image', className: 'bg-amber-100 text-amber-700' };
  return { label: 'Model Knowledge', className: 'bg-slate-100 text-slate-600' };
};

const HistoryGroupMobileList = ({
  group,
  originalItemsById,
  updatingId,
  updatingNameId,
  onOpenItemImage,
  onManualNameChange,
  onUpdateItemName,
  onPriceChange,
  onUpdatePrice,
}) => {
  return (
    <div className="sm:hidden divide-y divide-slate-100">
      {group.items.map((item) => {
        const originalItem = originalItemsById[item.id];
        const isDirty = originalItem && parseFloat(originalItem.actual_price) !== parseFloat(item.actual_price);
        const isUpdating = updatingId === item.id;
        const isNameDirty = originalItem && (originalItem.manual_name || '') !== (item.manual_name || '');
        const isNameUpdating = updatingNameId === item.id;
        const condition = item?.condition || 'Unknown';
        const source = sourceBadge(item?.price_source || 'model_knowledge');

        return (
          <div key={item.id} className="p-3 space-y-2">
            <button onClick={() => onOpenItemImage(item)} className="text-sm font-medium text-slate-700 hover:text-blue-600 hover:underline transition-colors text-left">
              {item.item_name}
            </button>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500 shrink-0">Manual:</span>
              <div className="relative flex items-center flex-1">
                <input
                  type="text"
                  placeholder="Custom name..."
                  value={item.manual_name || ''}
                  onChange={(e) => onManualNameChange(item.id, e.target.value)}
                  className={`w-full pr-8 py-1 px-2 border rounded text-sm focus:outline-none focus:ring-1 transition-all ${
                    isNameDirty ? 'border-blue-300 focus:ring-blue-500 bg-blue-50/30' : 'border-slate-200 focus:ring-green-500'
                  }`}
                />
                <button
                  onClick={() => onUpdateItemName(item.id, item.manual_name || '')}
                  disabled={!isNameDirty || isNameUpdating}
                  className={`absolute right-1 p-1 rounded transition-all ${
                    isNameUpdating ? 'text-slate-400' : (isNameDirty ? 'text-blue-600 hover:bg-blue-100' : 'text-green-500 cursor-default')
                  }`}
                >
                  {isNameUpdating ? <RefreshCcw size={14} className="animate-spin" /> : (isNameDirty ? <Save size={14} /> : <Check size={14} />)}
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm text-green-600 font-bold">${item.estimated_value?.toLocaleString()}</span>
              <span className="text-xs text-indigo-700 font-semibold">Conf: {formatConfidencePercent(item?.confidence_score)}</span>
              <div className="flex items-center gap-1">
                <span className="text-xs text-slate-500">Actual:</span>
                <div className="relative inline-flex items-center">
                  <span className="absolute left-2 text-slate-400 text-sm">$</span>
                  <input
                    type="number"
                    placeholder="0.00"
                    value={item.actual_price}
                    onChange={(e) => onPriceChange(item.id, e.target.value)}
                    className={`w-24 pl-5 pr-8 py-1 border rounded text-sm focus:outline-none focus:ring-1 transition-all [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                      isDirty ? 'border-blue-300 focus:ring-blue-500 bg-blue-50/30' : 'border-slate-200 focus:ring-green-500'
                    }`}
                    step="0.01"
                  />
                  <button
                    onClick={() => onUpdatePrice(item.id, item.actual_price)}
                    disabled={!isDirty || isUpdating}
                    className={`absolute right-1 p-1 rounded transition-all ${
                      isUpdating ? 'text-slate-400' : (isDirty ? 'text-blue-600 hover:bg-blue-100' : 'text-green-500 cursor-default')
                    }`}
                  >
                    {isUpdating ? <RefreshCcw size={14} className="animate-spin" /> : (isDirty ? <Save size={14} /> : <Check size={14} />)}
                  </button>
                </div>
              </div>
            </div>
            <PriceDistributionView
              rangeValue={item?.estimated_value_range}
              distributionValue={item?.price_estimate_distribution}
              maxBins={3}
              compact
            />
            <div className="flex items-center gap-2">
              <span className="text-[9px] text-slate-400">Condition:</span>
              <span className={`inline-block rounded px-1.5 py-0.5 text-[9px] font-semibold ${conditionBadgeClass(condition)}`}>
                {condition}
              </span>
              <span className="text-[9px] text-slate-400 ml-1">Price Src:</span>
              <span className={`inline-block rounded px-1.5 py-0.5 text-[9px] font-semibold ${source.className}`}>
                {source.label}
              </span>
            </div>
          </div>
        );
      })}

      {group.items.length >= 1 && (
        <div className="p-3 bg-slate-50 border-t border-slate-200 flex justify-between items-center">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Est. Sum</span>
          <span className="text-sm text-green-600 font-bold">
            ${group.items.reduce((sum, i) => sum + (parseFloat(i.estimated_value) || 0), 0).toLocaleString()}
          </span>
        </div>
      )}
    </div>
  );
};

export default HistoryGroupMobileList;
